import type { Route, RoutesResponse } from './types'
import { PSEUDO_ROUTES, isPseudoRoute } from './types'

const emptyRoute = (vehicle: string): Route => ({
  vehicle, driver: null, stops: [], visible: true, isTruckLocked: false,
  deliveryAssignee: null, optimized: false, showRoute: false, shouldCheckRoute: false,
})

const withDefaults = (r: Route): Route => ({
  ...emptyRoute(r.vehicle),
  ...r,
  stops: Array.isArray(r.stops) ? r.stops : [],
  showRoute: (r.stops?.length ?? 0) > 2,
  // PreRoute/ReadyToRoute/Unfit Stops are staging buckets, not trucks — they can't be
  // truck-locked. getRoutes derived the flag from any stop still carrying
  // vehicleLocked, which locked the whole bucket (locked outline, controls disabled).
  // Fixed at source too; this keeps the board right against a backend not yet deployed.
  isTruckLocked: isPseudoRoute(r.vehicle) ? false : (r.isTruckLocked ?? false),
})

/** Normalize a getRoutes/getFleets response, guaranteeing the three pseudo-routes. */
export const normalizeRoutes = (resp: RoutesResponse): RoutesResponse => {
  const routes = (resp.routes ?? []).map(withDefaults)
  for (const name of PSEUDO_ROUTES) {
    if (!routes.some((r) => r.vehicle === name)) routes.push(emptyRoute(name))
  }
  return { ...resp, routes }
}

const RANK: Record<string, number> = { PreRoute: 0, ReadyToRoute: 1 }
/** PreRoute, ReadyToRoute, then vehicles (alpha), then Unfit Stops last. */
export const orderRoutesForBoard = (routes: Route[]): Route[] =>
  [...routes].sort((a, b) => {
    const ra = a.vehicle === 'Unfit Stops' ? 99 : RANK[a.vehicle] ?? 2
    const rb = b.vehicle === 'Unfit Stops' ? 99 : RANK[b.vehicle] ?? 2
    if (ra !== rb) return ra - rb
    return a.vehicle.localeCompare(b.vehicle)
  })
